/** @format */
import {Box, Typography} from '@mui/material';
import {clsx} from 'clsx';
import {Trans} from 'react-i18next';

import CallSplitIcon from '@mui/icons-material/CallSplit';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import LegendToggleIcon from '@mui/icons-material/LegendToggle';
import SensorsIcon from '@mui/icons-material/Sensors';

import {useNavigate, useSearchParams} from 'react-router-dom';

import {CheckIcon} from '@storybook/icons';

import {SidebarStep} from 'types';

import {useGetCurrentUserQuery} from '../graphql/__generated__';
import {StyledBox, classes} from '../styles';

const getPath = (path: string, companyId: string | null) => {
  if (!companyId) return '';
  return `/home/project/${path}?companyId=${companyId}&edit=1`;
};

interface ExtendedSidebarStep extends SidebarStep {
  path: string;
}

interface TransformedStep {
  name: string | JSX.Element;
  path: string;
  key: 'CREATE' | 'MEDIAPLAN' | 'TYPE' | 'PERIODS' | 'SENSORS' | 'FEEDS' | 'FUNNEL';
  isVisible: boolean;
  icon?: React.ReactNode;
}

const STEPS: ExtendedSidebarStep[] = [
  {
    isVisible: true,
    name: 'Настройка проекта',
    key: 'CREATE',
    path: 'edit',
  },
  {
    isVisible: false,
    name: 'Медиаплан',
    key: 'MEDIAPLAN',
    path: 'type',
    icon: (
      <LegendToggleIcon className={clsx(classes.stepButtonIcon, classes.stepButtonIconHeading)} />
    ),
  },
  {
    isVisible: false,
    name: 'Тип кампаний',
    key: 'TYPE',
    path: 'type',
    icon: <CallSplitIcon className={clsx(classes.stepButtonIcon, classes.stepButtonIconRotated)} />,
  },
  {
    isVisible: false,
    name: 'Период, бюджет и KPI',
    key: 'PERIODS',
    path: 'periods',
    icon: <CurrencyRubleIcon className={classes.stepButtonIcon} />,
  },
  {
    isVisible: false,
    name: 'Сенсоры',
    key: 'SENSORS',
    path: 'sensors',
    icon: <SensorsIcon className={classes.stepButtonIcon} />,
  },
  {
    isVisible: true,
    name: 'Фиды',
    key: 'FEEDS',
    path: 'feeds',
  },
  {
    isVisible: true,
    name: 'Воронка',
    key: 'FUNNEL',
    path: 'funnel',
  },
];

interface Props {
  activeStep: string;
  // temporary flag for storybook
  enableAll?: boolean;
  onSaveAll?: (() => void) | null;
  projectName?: string;
}

const SidebarEdit = ({activeStep, enableAll, onSaveAll, projectName}: Props) => {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId');
  const navigate = useNavigate();

  const {data} = useGetCurrentUserQuery({
    variables: {id: companyId},
  });

  const getNameOfDeclaration = (step: ExtendedSidebarStep) => {
    const {key, name} = step;

    if (key === 'TYPE') {
      const mediaplanType = data?.currentUser?.company?.mediaplanType;

      const transformedType = mediaplanType === 'PERFORM' ? 'За клики' : 'За показы';

      return (
        <Box>
          <Typography>
            <Trans>Тип кампаний:</Trans>
          </Typography>
          <Typography>
            <Trans>{transformedType}</Trans>
          </Typography>
        </Box>
      );
    }

    return <Trans>{name}</Trans>;
  };

  const transformNames = (step: ExtendedSidebarStep): TransformedStep => {
    return {
      ...step,
      name: getNameOfDeclaration(step),
      path: getPath(step.path, companyId),
    };
  };

  const handleStepClick = (path: string) => {
    navigate(path);
  };

  // duplicate for storybook
  let steps = [...STEPS].map(transformNames);

  const isStepVisible = (step: TransformedStep) => enableAll || step.isVisible;

  const handleMainProjectClick = () => {
    navigate('/home/main/status');
  };

  steps = steps.filter(isStepVisible);

  return (
    <StyledBox>
      <Box className={classes.sidebar}>
        <Box
          onClick={handleMainProjectClick}
          className={clsx(classes.stepButton, classes.stepButtonProjectName)}
        >
          <Typography className={classes.projectTitle}>
            <Trans>Проект</Trans>
          </Typography>
          <Typography className={classes.projectName}>
            {projectName || data?.currentUser?.company?.name}
          </Typography>
        </Box>
        {steps.map(step => (
          <Box
            key={step.key}
            className={clsx(
              classes.stepButton,
              step.key === 'CREATE' && steps.length === 1 && classes.stepButtonCreate,
              step.key === 'MEDIAPLAN' && classes.stepButtonHeading,
              activeStep === step.key && classes.stepButtonActive,
            )}
            onClick={() => handleStepClick(step.path)}
            sx={{cursor: 'pointer'}}
          >
            {step.icon}
            {step.name}
          </Box>
        ))}
        {onSaveAll && (
          <Box className={clsx(classes.stepButton, classes.stepButtonSaveAll)} onClick={onSaveAll}>
            <CheckIcon className={classes.stepButtonIcon} />

            <Trans>Завершить настройку</Trans>
          </Box>
        )}
      </Box>
    </StyledBox>
  );
};

export default SidebarEdit;
